import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { usePlaceByRules } from "../hooks/usePlaceByRules";
import { placingsToConfirm } from "../util/placeByRules";
import PlaceByRulesProblems from "./PlaceByRulesProblems";

// #3567 -- "Place by the rules": shows what the auto-placer would place for the
// class and writes nothing until the person confirms. The confirm sends back
// exactly the placings shown; if the scores moved meanwhile the server says so
// (409 place_by_rules_changed) and the new proposal is shown to confirm again.
// A class that cannot be placed by rule says why and offers the fix (ADR 0004).
export default function PlaceByRulesDialog({
  classId,
  classNumber,
  showId,
  onClose,
  onPlaced,
  onNavigate,
  hideOpenPlacings = false,
}) {
  const { t } = useTranslation("scoreadmin");
  const { preview: fetchPreview, commit, committing } = usePlaceByRules();
  const [state, setState] = useState({ status: "loading", preview: null });
  const [changedNotice, setChangedNotice] = useState(false);
  const [commitFailed, setCommitFailed] = useState(false);

  const load = useCallback(async () => {
    setState({ status: "loading", preview: null });
    try {
      setState({ status: "ready", preview: await fetchPreview(classId) });
    } catch {
      setState({ status: "failed", preview: null });
    }
  }, [classId, fetchPreview]);

  useEffect(() => {
    load();
  }, [load]);

  const preview = state.preview;
  const num = classNumber ?? preview?.classNumber ?? "";

  const confirm = async () => {
    setCommitFailed(false);
    try {
      const result = await commit(classId, placingsToConfirm(preview));
      onPlaced?.(result);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "place_by_rules_changed") {
        // Scores moved since the preview: show the new proposal, nothing was written.
        setChangedNotice(true);
        await load();
      } else if (code === "place_by_rules_unavailable") {
        // The 409 carries the same view as the preview: say why, offer the fix.
        setChangedNotice(false);
        const view = err?.response?.data?.data;
        if (view && typeof view.placeable === "boolean") setState({ status: "ready", preview: view });
        else await load();
      } else {
        setCommitFailed(true);
      }
    }
  };

  return (
    <Dialog open onClose={() => !committing && onClose()} fullWidth maxWidth="xs" data-testid="place-by-rules-dialog">
      <DialogTitle>{t("placeByRules.dialogTitle", { classNumber: num })}</DialogTitle>
      <DialogContent>
        {state.status === "loading" && (
          <Stack direction="row" spacing={1} alignItems="center" data-testid="place-by-rules-loading">
            <CircularProgress size={18} />
            <Typography variant="body2">{t("placeByRules.checking")}</Typography>
          </Stack>
        )}
        {state.status === "failed" && (
          <Stack spacing={1.5}>
            <Alert severity="error" data-testid="place-by-rules-check-failed">
              {t("placeByRules.couldNotCheckRules", { classNumber: num })}
            </Alert>
            <Button variant="outlined" size="small" onClick={load} sx={{ alignSelf: "flex-start" }}>
              {t("placeByRules.tryAgain")}
            </Button>
          </Stack>
        )}
        {state.status === "ready" && (
          <Stack spacing={2}>
            {changedNotice && (
              <Alert severity="warning" data-testid="place-by-rules-changed">
                {t("placeByRules.scoresChanged")}
              </Alert>
            )}
            {preview.placeable ? (
              <>
                <Typography variant="body2">{t("placeByRules.intro")}</Typography>
                <Table size="small" data-testid="place-by-rules-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("placeByRules.placeColumn")}</TableCell>
                      <TableCell>{t("placeByRules.cnoColumn")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.placings.map((p) => (
                      <TableRow key={p.rideId} data-testid={`place-by-rules-row-${p.rideId}`}>
                        <TableCell>{p.placing}</TableCell>
                        <TableCell>{p.competitorNumber ?? t("placeByRules.rideWithoutCno")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("placeByRules.notPlacedYet", { classNumber: num })}
                </Typography>
                <PlaceByRulesProblems
                  preview={preview}
                  classNumber={classNumber}
                  showId={showId}
                  hideOpenPlacings={hideOpenPlacings}
                  onNavigate={() => {
                    onNavigate?.();
                    onClose();
                  }}
                />
              </>
            )}
            {commitFailed && (
              <Alert severity="error" data-testid="place-by-rules-commit-failed">
                {t("placeByRules.commitFailed")}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={committing} data-testid="place-by-rules-cancel">
          {state.status === "ready" && preview.placeable ? t("placeByRules.cancel") : t("placeByRules.close")}
        </Button>
        {state.status === "ready" && preview.placeable && (
          <Button
            variant="contained"
            onClick={confirm}
            loading={committing}
            disabled={committing}
            data-testid="place-by-rules-confirm"
          >
            {t("placeByRules.confirm", { classNumber: num })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
